/**
 * The one place the app talks to the backend.
 *
 * Session state is a httpOnly cookie the browser sends on its own, so nothing
 * here handles tokens. Two things every call needs and nobody should have to
 * remember: `credentials: 'include'` (the cookie is cross-origin during dev,
 * same-origin in production) and the `X-Requested-With` header the server
 * demands on every mutation as its CSRF gate — a cross-site form cannot set a
 * custom header, so a forged POST dies at the filter.
 *
 * Errors arrive as RFC-7807 problem+json; they become `ApiError`, which carries
 * the status, the server's detail and — for the cases the UI branches on — a
 * machine-readable code.
 */
import { errorText } from './error-text'

const BASE = '/api'
const CSRF_HEADER = 'X-Requested-With'
const CSRF_VALUE = 'hub4psy'

/** Statuses worth one more try: the request itself was fine, the moment was not. */
const TRANSIENT = new Set([0, 429, 502, 503, 504])

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** e.g. EMAIL_NOT_VERIFIED — set by the server for branchable cases. */
    readonly code?: string,
    /** Present on 409 from a scheme save: the revision the server holds. */
    readonly currentRev?: number,
    /** Present on 429: seconds until the next attempt is allowed. */
    readonly retryAfter?: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isConflict(): boolean {
    return this.status === 409
  }

  /**
   * The failure is about the moment, not the request: the pool was full, the
   * container was restarting, the network dropped. Screens show «try again»
   * for these instead of the server's detail.
   */
  get isTransient(): boolean {
    return TRANSIENT.has(this.status)
  }
}

/**
 * One sentence for a failed call, in the interface language. Transient
 * failures all read the same — the person cannot act on «502 Bad Gateway»,
 * and the honest advice is identical for every one of them. Everything else
 * shows the server's detail, which is written for people.
 */
export function describeError(error: unknown, lang: 'ru' | 'en'): string {
  return errorText(error, lang)
}

type Listener = () => void
const unauthorizedListeners = new Set<Listener>()

/**
 * Fires when any call comes back 401 — the session store listens and drops the
 * user to the sign-in screen. Without this, a cookie that expired mid-session
 * would leave every screen quietly empty.
 */
export function onUnauthorized(listener: Listener): () => void {
  unauthorizedListeners.add(listener)
  return () => unauthorizedListeners.delete(listener)
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  /** Sent as multipart instead of JSON. */
  form?: FormData
  query?: Record<string, string | number | boolean | undefined>
  signal?: AbortSignal
  /** A 401 here is expected (the boot-time /me probe) — do not broadcast it. */
  quiet401?: boolean
  /** Ask the browser to finish this call even if the page dies (autosave
   *  flush on pagehide). JSON bodies only — keepalive caps the body at 64 KB. */
  keepalive?: boolean
  /** `no-cache` re-asks the server past the browser's copy: public reads are
   *  cached for a minute, and right after one's own write that copy is stale. */
  cache?: RequestCache
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET'
  const url = BASE + path + queryString(options.query)

  const headers: Record<string, string> = { [CSRF_HEADER]: CSRF_VALUE }
  let body: BodyInit | undefined
  if (options.form) {
    body = options.form // browser sets the multipart boundary itself
  } else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(options.body)
  }

  const response = await fetch(url, {
    method,
    headers,
    body,
    credentials: 'include',
    signal: options.signal,
    keepalive: options.keepalive,
    cache: options.cache,
  })

  if (response.status === 401 && !options.quiet401) {
    unauthorizedListeners.forEach((listener) => listener())
  }

  if (!response.ok) {
    throw await toError(response)
  }
  if (response.status === 204 || response.status === 202) {
    return undefined as T
  }
  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}

/** Binary read — attachment downloads, which are not JSON. */
export async function requestBlob(path: string): Promise<Blob> {
  const response = await fetch(BASE + path, {
    headers: { [CSRF_HEADER]: CSRF_VALUE },
    credentials: 'include',
  })
  if (response.status === 401) {
    unauthorizedListeners.forEach((listener) => listener())
  }
  if (!response.ok) {
    throw await toError(response)
  }
  return response.blob()
}

async function toError(response: Response): Promise<ApiError> {
  let detail = response.statusText || `HTTP ${response.status}`
  let code: string | undefined
  let currentRev: number | undefined
  try {
    const problem = (await response.json()) as {
      detail?: string
      title?: string
      code?: string
      currentRev?: number
    }
    detail = problem.detail ?? problem.title ?? detail
    code = problem.code
    currentRev = problem.currentRev
  } catch {
    // Not problem+json (a proxy error page, an empty body) — keep the status text.
  }
  const retryAfterHeader = response.headers.get('Retry-After')
  return new ApiError(
    response.status,
    detail,
    code,
    currentRev,
    retryAfterHeader ? Number(retryAfterHeader) : undefined,
  )
}

function queryString(query: RequestOptions['query']): string {
  if (!query) return ''
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value))
  }
  const encoded = params.toString()
  return encoded ? `?${encoded}` : ''
}

/** Server pages are `{ items, nextCursor }`. */
export interface Page<T> {
  items: T[]
  nextCursor: string | null
}
